/**
 * Pricing Service
 *
 * Fetches pricing configuration from the backend.
 * No authentication required - public endpoint for landing page.
 */

import { API_URL } from '../../config';
import { createRetryFetch } from '../errorHandling';
import { errorHandlingService, type ErrorHandlingResponse } from './fallbackService';

export interface PricingConfig {
  articleBasePrice: number;
  currencyCode: string;
  currencySymbol: string;
}

export class PricingService {
  private readonly baseUrl: string;
  private readonly retryFetch: typeof fetch;

  constructor(baseUrl: string = API_URL) {
    this.baseUrl = baseUrl;
    this.retryFetch = createRetryFetch({
      maxAttempts: 2,
      baseDelay: 500,
      maxDelay: 2000,
    });
  }

  /**
   * Get pricing configuration (public endpoint, no auth)
   */
  async getPricing(): Promise<ErrorHandlingResponse<PricingConfig>> {
    const url = `${this.baseUrl}/api/pricing`;

    const result = await errorHandlingService.withErrorHandling(
      async () => {
        const response = await this.retryFetch(url);
        const data = await response.json();

        if (!data.success) {
          throw new Error('Failed to fetch pricing');
        }

        return data.data;
      },
      {
        errorMessage: 'Unable to load pricing',
      }
    );

    // If error, provide fallback data
    if (!result.success) {
      return {
        success: true,
        data: {
          articleBasePrice: 49.00,
          currencyCode: 'USD',
          currencySymbol: '$',
        }
      };
    }

    return result;
  }
}
